/* Decorative effects only: native Dawn buttons keep every purchase action. */
(() => {
  if (window.__craftProductEffects) return;
  window.__craftProductEffects = true;

  const svgNS = 'http://www.w3.org/2000/svg';
  const controllers = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const primarySelector = '#MainContent product-info .product-form__submit, craft-product-sticky .cp-sticky__button';
  const paymentSelector = '#MainContent product-info .shopify-payment-button';
  const optionsSelector = '#MainContent product-info .shopify-payment-button__more-options';
  let scheduled = false;

  const motionAllowed = () => document.body?.dataset.cbMotion !== 'false' && !reducedMotion.matches;
  const svgElement = (name, attributes = {}) => {
    const element = document.createElementNS(svgNS, name);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    return element;
  };

  function nativeCartIcon() {
    const source = document.querySelector('template[data-cp-purchase-icon]')?.content.querySelector('svg') ||
      document.querySelector('#cart-icon-bubble svg');
    if (!source) return null;
    const icon = source.cloneNode(true);
    [icon, ...icon.querySelectorAll('[id]')].forEach(element => element.removeAttribute('id'));
    icon.querySelectorAll('title, desc').forEach(element => element.remove());
    icon.setAttribute('class', 'cp-effects-cart');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.setAttribute('data-cp-effect-decor', 'icon');
    icon.removeAttribute('role');
    icon.removeAttribute('aria-label');
    icon.removeAttribute('aria-labelledby');
    return icon;
  }

  function glitterLayer() {
    const layer = svgElement('svg', {
      class: 'cp-effects-glitter', width: '100%', height: '100%',
      'aria-hidden': 'true', focusable: 'false', 'data-cp-effect-decor': 'glitter'
    });
    const locations = [['8%', '-6', 'ice'], ['86%', '-5', 'cyan'], ['19%', '83%', 'periwinkle'], ['76%', '81%', 'ice']];
    locations.forEach(([x, y, color], index) => {
      const star = svgElement('svg', { x, y, width: '15', height: '15', viewBox: '0 0 20 20', class: `cp-effects-star cp-effects-star--${color}` });
      star.style.setProperty('--cp-star-delay', `${index * 45}ms`);
      star.appendChild(svgElement('path', { d: 'M10 1 12.4 7.6 19 10 12.4 12.4 10 19 7.6 12.4 1 10 7.6 7.6Z' }));
      layer.appendChild(star);
    });
    return layer;
  }

  function sheenLayer() {
    const layer = svgElement('svg', {
      class: 'cp-effects-sheen', viewBox: '0 0 100 100', preserveAspectRatio: 'none',
      'aria-hidden': 'true', focusable: 'false', 'data-cp-effect-decor': 'sheen'
    });
    layer.appendChild(svgElement('path', { class: 'cp-effects-sheen-band', d: 'M-45 0H-23L23 100H1Z' }));
    return layer;
  }

  class PurchaseEffect {
    constructor(element, kind) {
      this.element = element;
      this.kind = kind;
      this.className = `cp-effects-${kind}`;
      this.element.classList.add(this.className);
      this.lastPulse = -Infinity;
      this.entered = false;
      this.visible = false;
      this.autoInterval = 4000;
      this.onInteraction = () => this.pulse();
      this.element.addEventListener('pointerenter', this.onInteraction);
      this.element.addEventListener('pointerdown', this.onInteraction, { passive: true });
      this.element.addEventListener('focusin', this.onInteraction);
      this.stateObserver = new MutationObserver(() => this.sync());
      this.stateObserver.observe(element.closest('product-form') || element, {
        attributes: true, subtree: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class', 'hidden']
      });
      this.refresh();
      visibilityObserver?.observe(element);
      if (!visibilityObserver) this.updateFallbackVisibility();
    }

    refresh() {
      // Append SVGs after the label and spinner: Dawn selects the first span.
      if (this.kind === 'primary') {
        if (!this.element.querySelector('[data-cp-effect-decor="icon"]')) {
          const icon = nativeCartIcon();
          if (icon) this.element.appendChild(icon);
        }
        if (!this.element.querySelector('[data-cp-effect-decor="glitter"]')) this.element.appendChild(glitterLayer());
        if (!this.element.querySelector('[data-cp-effect-decor="sheen"]')) this.element.appendChild(sheenLayer());
      }
      this.sync();
    }

    available() {
      const nativeButton = this.element.closest('product-form')?.querySelector('.product-form__submit');
      const ready = button => !button.disabled && button.getAttribute('aria-disabled') !== 'true' &&
        button.getAttribute('aria-busy') !== 'true' && !button.classList.contains('loading');
      return this.element.isConnected && !this.element.hidden && ready(this.element) &&
        (!nativeButton || ready(nativeButton)) && !this.element.closest('[hidden], [aria-hidden="true"]');
    }

    active() {
      return motionAllowed() && !document.hidden && this.visible && this.available();
    }

    sync() {
      if (!this.active()) {
        this.stop();
        clearTimeout(this.autoTimer);
        this.autoTimer = null;
        return;
      }
      this.scheduleAutomatic();
    }

    setVisible(visible) {
      this.visible = visible;
      this.sync();
      if (visible && !this.entered && this.active()) {
        this.entered = true;
        this.pulse();
      }
    }

    updateFallbackVisibility() {
      const bounds = this.element.getBoundingClientRect();
      this.setVisible(bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight &&
        bounds.right > 0 && bounds.left < window.innerWidth);
    }

    scheduleAutomatic() {
      if (this.autoTimer || !this.active()) return;
      this.autoTimer = setTimeout(() => {
        this.autoTimer = null;
        if (!this.active()) return;
        this.pulse();
        this.scheduleAutomatic();
      }, this.autoInterval);
    }

    pulse() {
      if (!this.active() || performance.now() - this.lastPulse < 1200) return;
      this.lastPulse = performance.now();
      this.element.classList.add('cp-effects-run');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.stop(), 1050);
    }

    stop() {
      clearTimeout(this.timer);
      if (this.element.classList.contains('cp-effects-run')) this.element.classList.remove('cp-effects-run');
    }

    destroy() {
      this.stop();
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
      visibilityObserver?.unobserve(this.element);
      this.stateObserver.disconnect();
      this.element.removeEventListener('pointerenter', this.onInteraction);
      this.element.removeEventListener('pointerdown', this.onInteraction);
      this.element.removeEventListener('focusin', this.onInteraction);
      this.element.querySelectorAll('[data-cp-effect-decor]').forEach(element => element.remove());
      this.element.classList.remove(this.className);
      controllers.delete(this.element);
    }
  }

  const visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const controller = controllers.get(entry.target);
      controller?.setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.2);
    });
  }, { threshold: [0, 0.2] }) : null;

  function updateMotion() {
    document.documentElement.classList.toggle('cp-effects-motion', motionAllowed());
    controllers.forEach(controller => controller.sync());
  }

  function scan() {
    scheduled = false;
    controllers.forEach(controller => {
      if (!controller.element.isConnected) controller.destroy();
    });
    [[primarySelector, 'primary'], [paymentSelector, 'payment'], [optionsSelector, 'more-options']].forEach(([selector, kind]) => {
      document.querySelectorAll(selector).forEach(element => {
        if (element.closest('quick-add-modal, product-modal')) return;
        const current = controllers.get(element);
        if (current) current.refresh();
        else controllers.set(element, new PurchaseEffect(element, kind));
      });
    });
  }

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scan);
  }

  function initialize() {
    updateMotion();
    scan();
    const pageObserver = new MutationObserver(records => {
      if (records.some(record => record.type === 'attributes' && record.target === document.body)) updateMotion();
      if (records.some(record => record.type === 'childList')) scheduleScan();
    });
    pageObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-cb-motion'] });
    reducedMotion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', () => controllers.forEach(controller => controller.sync()));
    if (!visibilityObserver) {
      const updateVisibleControls = () => controllers.forEach(controller => controller.updateFallbackVisibility());
      window.addEventListener('scroll', updateVisibleControls, { passive: true });
      window.addEventListener('resize', updateVisibleControls, { passive: true });
    }
    document.addEventListener('shopify:section:load', scheduleScan);
    document.addEventListener('shopify:section:unload', event => {
      controllers.forEach(controller => {
        const stickySection = controller.element.closest('craft-product-sticky')?.dataset.sectionId;
        if (event.target.contains(controller.element) || stickySection === event.detail?.sectionId) controller.destroy();
      });
      scheduleScan();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
